import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useModules, LEDGER_ROUTE_MODULE } from "@/context/ModulesContext";
import { AssistantChat } from "@/components/AssistantChat";
import HelpDrawer from "@/components/HelpDrawer";
import ImportIndicator from "@/components/ImportIndicator";
import GuidedFlow from "@/components/GuidedFlow";
import { NotificationBell } from "@/components/NotificationBell";
import { inboxTotal } from "@/lib/inbox";
import {
  SquaresFour, Sliders, ListChecks, ClockCounterClockwise, SignOut, Scales, ChartLineUp, Buildings, CaretDown, CaretRight, Files, Lightbulb, SealCheck, ShieldCheck, CurrencyGbp, Cube, ChartPieSlice, Receipt, TrendUp, Sparkle, Faders, Target, Coins, Question, CreditCard, UserCircle, WarningCircle, CaretDoubleLeft, CaretDoubleRight, GraduationCap, MagnifyingGlass, Table, ArrowsDownUp, Tray, Gauge,
} from "@phosphor-icons/react";

const NAV_TOP = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour, id: "nav-dashboard" },
  { to: "/inbox", label: "Action Inbox", icon: Tray, id: "nav-inbox" },
];

const CLIENTS_NAV = { to: "/portfolio", label: "Clients", icon: ChartPieSlice, id: "nav-portfolio" };
const COMMAND_CENTRE_NAV = { to: "/command-centre", label: "Command Centre", icon: Gauge, id: "nav-command-centre" };

const NAV_GROUPS = [
  { title: "Ledgers", items: [
    { to: "/accruals", label: "Accruals", icon: TrendUp, id: "nav-accruals" },
    { to: "/prepayments", label: "Prepayments", icon: Receipt, id: "nav-prepayments" },
    { to: "/deferred-income", label: "Deferred Income", icon: CurrencyGbp, id: "nav-deferred-revenue" },
    { to: "/fixed-assets", label: "Fixed Assets", icon: Cube, id: "nav-fixed-assets" },
  ]},
  { title: "Month-end", items: [
    { to: "/unscheduled", label: "To Review", icon: ListChecks, id: "nav-unscheduled" },
    { to: "/reconciliation", label: "Reconciliation", icon: Scales, id: "nav-reconciliation" },
    { to: "/approvals", label: "Approvals", icon: ShieldCheck, id: "nav-approvals" },
    { to: "/close", label: "Close", icon: SealCheck, id: "nav-close" },
  ]},
  { title: "Automation", items: [
    { to: "/recurring-accruals", label: "Recurring Accruals", icon: Sparkle, id: "nav-recurring-accruals" },
    { to: "/treatment-rules", label: "Treatment Rules", icon: Faders, id: "nav-treatment-rules" },
  ]},
  { title: "Insights", items: [
    { to: "/insights", label: "Insights", icon: Lightbulb, id: "nav-insights" },
    { to: "/accrual-accuracy", label: "Estimate Accuracy", icon: Target, id: "nav-accrual-accuracy" },
  ]},
  { title: "Reports", items: [
    { to: "/reports", label: "Reports", icon: Files, id: "nav-reports" },
    { to: "/movement", label: "Movement Report", icon: ArrowsDownUp, id: "nav-movement" },
    { to: "/deferred-reports", label: "Revenue Recognition", icon: Coins, id: "nav-deferred-reports" },
    { to: "/forecast", label: "12-Month Forecast", icon: ChartLineUp, id: "nav-forecast" },
    { to: "/audit", label: "Audit History", icon: ClockCounterClockwise, id: "nav-audit" },
  ]},
];

const ORG_BOTTOM = [
  { to: "/settings", label: "Client Settings", icon: Sliders, id: "nav-settings" },
];

const PRACTICE_BOTTOM = [
  { to: "/practice", label: "Practice Settings", icon: Buildings, id: "nav-practice", admin: true, practiceOnly: true },
  { to: "/subscription", label: "Subscription", icon: CreditCard, id: "nav-subscription", practiceOnly: true },
  { to: "/learning", label: "Learning", icon: GraduationCap, id: "nav-learning" },
  { to: "/help", label: "Help", icon: Question, id: "nav-help" },
];

// Routes that don't change the practice/org workspace (reachable from both).
const NEUTRAL_ROUTES = ["/practice", "/subscription", "/account", "/help", "/learning"];

export const FLAT_NAV = [
  COMMAND_CENTRE_NAV, CLIENTS_NAV, ...NAV_TOP, ...NAV_GROUPS.flatMap((g) => g.items), ...ORG_BOTTOM, ...PRACTICE_BOTTOM,
];

// Map each app route to the most relevant Help Centre guide (section-index).
const ROUTE_HELP = {
  "/portfolio": "Administration-0",
  "/command-centre": "Administration-3",
  "/dashboard": "Day-to-day-0",
  "/unscheduled": "Day-to-day-0",
  "/accruals": "Day-to-day-1",
  "/prepayments": "Day-to-day-1",
  "/deferred-income": "Month-end-2",
  "/fixed-assets": "Day-to-day-1",
  "/recurring-accruals": "Day-to-day-2",
  "/treatment-rules": "Getting set up-1",
  "/approvals": "Day-to-day-3",
  "/insights": "Day-to-day-5",
  "/reconciliation": "Month-end-0",
  "/accrual-accuracy": "Month-end-1",
  "/prepayment-variance": "Month-end-1",
  "/deferred-income-variance": "Month-end-1",
  "/deferred-reports": "Month-end-2",
  "/close": "Month-end-3",
  "/reports": "Month-end-4",
  "/forecast": "Month-end-5",
  "/settings": "Getting set up-0",
};

function PastDueBanner({ account }) {
  const [loading, setLoading] = useState(false);
  if (!account || account.status !== "past_due") return null;
  const daysLeft = account.grace_until
    ? Math.max(0, Math.ceil((new Date(account.grace_until).getTime() - Date.now()) / 86400000))
    : null;
  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin, flow: "payment_method_update" });
      if (data.url) window.location.href = data.url;
      else setLoading(false);
    } catch {
      setLoading(false);
    }
  };
  return (
    <div data-testid="past-due-banner"
      className="sticky top-0 z-30 flex flex-wrap items-center justify-center gap-3 bg-[#E5484D] text-white text-sm font-medium px-4 py-2 shadow-sm">
      <WarningCircle size={16} weight="fill" />
      <span>
        {account.is_owner
          ? "We couldn't take your last payment — update your card to keep your account active."
          : "A payment issue needs attention — please ask your account owner to update the billing card."}
      </span>
      {daysLeft !== null && (
        <span data-testid="past-due-countdown"
          className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${daysLeft <= 2 ? "bg-[#7f0d11] text-white animate-pulse ring-1 ring-white/50" : "bg-white/20"}`}>
          {daysLeft === 0 ? "Access ends today" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} of access left`}
        </span>
      )}
      {account.is_owner && (
        <button onClick={openPortal} disabled={loading} data-testid="past-due-update-card"
          className="ml-1 px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-xs font-semibold uppercase tracking-wide disabled:opacity-60">
          {loading ? "Opening…" : "Update card"}
        </button>
      )}
    </div>
  );
}


function OnboardingNudge({ account, pathname, onGo }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("lottie_onboarding_nudge_dismissed") === "1");
  useEffect(() => {
    if (!account) return;
    api.get("/onboarding/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, [account, pathname]);
  if (!account || !status || status.complete || dismissed) return null;
  if (pathname === "/onboarding" || pathname === "/subscription") return null;
  const LABELS = {
    connect: "Connect your Xero organisation to get started",
    select: "Choose the organisation you'd like to manage",
    codes: "Tag your nominal codes so Lottie can build your register",
    import: "Import from Xero to finish your setup",
  };
  const dismiss = () => { sessionStorage.setItem("lottie_onboarding_nudge_dismissed", "1"); setDismissed(true); };
  return (
    <div data-testid="onboarding-nudge"
      className="sticky top-0 z-30 flex flex-wrap items-center justify-center gap-3 bg-[#0E103F] text-white text-sm font-medium px-4 py-2 shadow-sm">
      <ListChecks size={16} weight="fill" style={{ color: "#F37A24" }} />
      <span data-testid="onboarding-nudge-label">Finish setting up — {LABELS[status.step] || "complete your setup"}.</span>
      <button onClick={onGo} data-testid="onboarding-nudge-cta"
        className="ml-1 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F37A24] hover:bg-[#F37A24]/90 transition-colors text-xs font-semibold uppercase tracking-wide">
        Finish setup <CaretRight size={12} weight="bold" />
      </button>
      <button onClick={dismiss} data-testid="onboarding-nudge-dismiss"
        className="text-white/60 hover:text-white text-xs font-semibold ml-1" title="Dismiss for now">✕</button>
    </div>
  );
}



function TrialCountdownBanner({ account, onManage }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  if (!account || account.status !== "trialing" || !account.trial_ends_at) return null;
  const ms = Math.max(0, new Date(account.trial_ends_at).getTime() - now);
  const totalHours = Math.floor(ms / 3600000);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const urgent = ms <= 2 * 86400000;
  let label;
  if (ms <= 0) label = "Your free trial has ended";
  else if (days >= 1) label = `${days} day${days === 1 ? "" : "s"}${hours ? ` ${hours}h` : ""} left in your trial`;
  else if (totalHours >= 1) label = `${totalHours} hour${totalHours === 1 ? "" : "s"} left in your trial`;
  else label = "Less than an hour left in your trial";
  return (
    <div data-testid="trial-banner"
      className={`sticky top-0 z-30 flex flex-wrap items-center justify-center gap-3 text-sm font-medium px-4 py-2 shadow-sm ${urgent ? "bg-[#F37A24] text-white" : "bg-navy text-white"}`}>
      <Sparkle size={15} weight="fill" style={{ color: urgent ? "#ffffff" : "#F37A24" }} />
      <span data-testid="trial-countdown">{label} — pick a plan to keep Lottie running.</span>
      <button onClick={onManage} data-testid="trial-banner-cta"
        className={`ml-1 px-3 py-1 rounded-full transition-colors text-xs font-semibold uppercase tracking-wide ${urgent ? "bg-white text-[#F37A24] hover:bg-white/90" : "bg-[#F37A24] hover:bg-[#F37A24]/90 text-white"}`}>
        Pick your plan
      </button>
    </div>
  );
}



export default function Layout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, account, logout } = useAuth();
  const { modules } = useModules();
  const [logo, setLogo] = useState(null);
  const [badges, setBadges] = useState({});
  const [demoMode, setDemoMode] = useState(false);
  const [workspace, setWorkspace] = useState(() => localStorage.getItem("lottie_workspace") || "practice");
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem("lottie_sidebar_collapsed");
    if (stored === "1") return true;
    if (stored === "0") return false;
    return window.innerWidth < 1024;
  });
  useEffect(() => {
    const onResize = () => {
      if (localStorage.getItem("lottie_sidebar_collapsed") != null) return;
      setCollapsed(window.innerWidth < 1024);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const toggleCollapsed = () => setCollapsed((c) => {
    const n = !c;
    try { localStorage.setItem("lottie_sidebar_collapsed", n ? "1" : "0"); } catch { /* noop */ }
    return n;
  });
  const inOrg = workspace === "org";
  // Scope the Ledgers nav to the active org's enabled modules (hide switched-off ledgers).
  const navGroups = useMemo(() => {
    const groups = modules
      ? NAV_GROUPS.map((g) => g.title === "Ledgers"
          ? { ...g, items: g.items.filter((it) => !LEDGER_ROUTE_MODULE[it.to] || modules[LEDGER_ROUTE_MODULE[it.to]]) }
          : g)
      : NAV_GROUPS;
    return groups.filter((g) => g.items.length > 0);
  }, [modules]);
  const [openGroups, setOpenGroups] = useState(() => {
    if (localStorage.getItem("lottie_collapse_nav")) {
      localStorage.removeItem("lottie_collapse_nav");
      const collapsed = {};
      NAV_GROUPS.forEach((g) => { collapsed[g.title] = false; });
      localStorage.setItem("navGroups", JSON.stringify(collapsed));
      return collapsed;
    }
    try { const s = JSON.parse(localStorage.getItem("navGroups")); if (s) return s; } catch { /* noop */ }
    const init = {};
    NAV_GROUPS.forEach((g) => { init[g.title] = g.items.some((it) => it.to === loc.pathname); });
    return init;
  });

  useEffect(() => {
    api.get("/settings").then(({ data }) => setLogo(data.settings.logo || null)).catch(() => {});
  }, []);

  // Practice vs organisation workspace: /portfolio is always practice; entering any org
  // route switches to org; neutral routes (practice/subscription/account/help) keep mode.
  useEffect(() => {
    let next = null;
    if (loc.pathname === "/portfolio" || loc.pathname === "/command-centre") next = "practice";
    else if (!NEUTRAL_ROUTES.includes(loc.pathname)) next = "org";
    if (next) {
      setWorkspace((w) => (w === next ? w : next));
      localStorage.setItem("lottie_workspace", next);
    }
  }, [loc.pathname]);

  useEffect(() => {
    const g = NAV_GROUPS.find((grp) => grp.items.some((it) => it.to === loc.pathname));
    if (!g) return;
    // Ensure the active group is open, but leave any other open groups as the user left them.
    setOpenGroups((prev) => {
      if (prev[g.title]) return prev;
      const next = { ...prev, [g.title]: true };
      try { localStorage.setItem("navGroups", JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, [loc.pathname]);

  const toggleGroup = (title) => setOpenGroups((prev) => {
    const next = { ...prev, [title]: !prev[title] };
    try { localStorage.setItem("navGroups", JSON.stringify(next)); } catch { /* noop */ }
    return next;
  });

  const allOpen = NAV_GROUPS.every((g) => openGroups[g.title]);
  const setAllGroups = (val) => {
    const next = {};
    NAV_GROUPS.forEach((g) => { next[g.title] = val; });
    setOpenGroups(next);
    try { localStorage.setItem("navGroups", JSON.stringify(next)); } catch { /* noop */ }
  };

  useEffect(() => {
    const loadBadges = () => api.get("/dashboard/overview").then(({ data }) => {
      // "to post" badge = postable now (matches the Close page), falling back to due+overdue.
      const post = data.journals_to_post != null ? data.journals_to_post
        : (((data.journals_overdue && data.journals_overdue.count) || 0)
          + ((data.journals_due && data.journals_due.count) || 0));
      setBadges((b) => ({
        ...b,
        "/inbox": inboxTotal(data),
        "/unscheduled": data.unscheduled_count || 0,
        "/approvals": data.approvals_waiting || 0,
        "/recurring-accruals": data.recurring_suggestions || 0,
        "/close": post,
        "/reconciliation": data.reconciliation_variances || 0,
        "/audit": data.recently_removed_count || 0,
      }));
      setDemoMode(!!data.demo_mode);
    }).catch(() => {});
    loadBadges();
    window.addEventListener("badges:refresh", loadBadges);
    return () => window.removeEventListener("badges:refresh", loadBadges);
  }, [loc.pathname]);

  // Command Centre "clients needing review" badge — fetched on mount and on explicit refresh
  // (e.g. after marking an org reviewed), not on every navigation, to avoid rebuilding it each time.
  useEffect(() => {
    const loadCC = () => api.get("/command-centre/unreviewed-count")
      .then(({ data }) => setBadges((b) => ({ ...b, "/command-centre": data.unreviewed || 0 })))
      .catch(() => {});
    loadCC();
    window.addEventListener("badges:refresh", loadCC);
    return () => window.removeEventListener("badges:refresh", loadCC);
  }, []);

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-appbg">
      <aside className={`${collapsed ? "w-20" : "w-64"} bg-navy text-white flex flex-col fixed h-screen z-20 border-r border-black/20 transition-[width] duration-200 overflow-y-auto sidebar-scroll`} data-testid="app-sidebar" data-collapsed={collapsed}>
        <div className={`${collapsed ? "px-2" : "px-5"} py-5 border-b border-white/10 relative`}>
          {logo && !collapsed ? (
            <div className="bg-white rounded-xl px-3 py-2.5 flex items-center justify-center" data-testid="sidebar-logo">
              <img src={logo} alt="Practice logo" className="max-h-12 max-w-full object-contain" />
            </div>
          ) : (
            <div className="bg-white rounded-xl px-2 py-2 flex items-center justify-center" data-testid="sidebar-lottie-logo">
              <img src="/lottie-logo.png" alt="Lottie" className={`${collapsed ? "max-h-8" : "max-h-14"} max-w-full object-contain`} />
            </div>
          )}
        </div>
        <div className="px-2 pt-3 pb-2 border-b border-white/10 space-y-2">
          <div className={`flex items-center gap-2 ${collapsed ? "flex-col" : ""}`}>
            <button onClick={() => window.dispatchEvent(new Event("command-palette:open"))}
              data-testid="sidebar-search" title="Search (⌘K)" aria-label="Search"
              className={`flex items-center ${collapsed ? "w-full justify-center px-2" : "flex-1 gap-2 px-3"} py-2 rounded-lg text-sm text-white/60 bg-white/5 hover:bg-white/10 hover:text-white transition-colors`}>
              <MagnifyingGlass size={collapsed ? 20 : 16} />
              {!collapsed && (<><span className="flex-1 text-left">Search</span>
                <kbd className="text-[0.6rem] font-semibold px-1.5 py-0.5 rounded bg-white/10 border border-white/10 text-white/50">⌘K</kbd></>)}
            </button>
            <NotificationBell collapsed={collapsed} />
          </div>
          <NavLink n={COMMAND_CENTRE_NAV} active={loc.pathname === COMMAND_CENTRE_NAV.to} badge={badges[COMMAND_CENTRE_NAV.to]} collapsed={collapsed} />
          <NavLink n={CLIENTS_NAV} active={loc.pathname === CLIENTS_NAV.to} collapsed={collapsed} />
        </div>
        <OrgSwitcher inOrg={inOrg} collapsed={collapsed} />
        {inOrg ? (
        collapsed ? (
        <nav className="flex-1 shrink-0 py-2 px-2 flex flex-col overflow-visible" data-testid="sidebar-nav-collapsed">
          <div className="space-y-1 mb-2">
            {NAV_TOP.map((n) => (
              <NavLink key={n.to} n={n} active={loc.pathname === n.to} badge={badges[n.to]} collapsed />
            ))}
          </div>
          <div className="space-y-1">
            {navGroups.map((g) => (
              <CollapsedGroup key={g.title} g={g} badges={badges} activePath={loc.pathname} />
            ))}
          </div>
        </nav>
        ) : (
        <nav className="shrink-0 py-2 px-3 flex flex-col" data-testid="sidebar-nav-expanded">
          <div className="sticky top-0 z-10 bg-navy pb-2 mb-1 space-y-1 border-b border-white/10">
            {NAV_TOP.map((n) => (
              <NavLink key={n.to} n={n} active={loc.pathname === n.to} badge={badges[n.to]} />
            ))}
          </div>
          <button onClick={() => setAllGroups(!allOpen)} data-testid="nav-toggle-all"
            className="self-end flex items-center gap-1 mt-2 mr-1 text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-white/35 hover:text-white/70 transition-colors">
            {allOpen ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
          {navGroups.map((g) => {
            const isOpen = openGroups[g.title];
            const groupBadge = g.items.reduce((s, it) => s + (badges[it.to] || 0), 0);
            return (
              <div key={g.title} className="mt-2">
                <button onClick={() => toggleGroup(g.title)} data-testid={`nav-group-${g.title.toLowerCase()}`}
                  aria-expanded={isOpen}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.06em] transition-colors duration-150 ${isOpen ? "text-[#F37A24]" : "text-white/75 hover:text-white"}`}>
                  <CaretRight size={11} weight="bold" className={`transition-transform duration-200 ${isOpen ? "text-[#F37A24] rotate-90" : "text-white/40"}`} />
                  <span className="flex-1 text-left">
                    {g.title}
                  </span>
                  {!isOpen && groupBadge > 0 && (
                    <span data-testid={`nav-group-badge-${g.title.toLowerCase()}`}
                      className="min-w-[16px] h-[16px] px-1 rounded-full bg-orange text-white text-[0.56rem] font-bold flex items-center justify-center">
                      {groupBadge > 99 ? "99+" : groupBadge}
                    </span>
                  )}
                </button>
                {isOpen && (
                  <div className="space-y-1 mt-1">
                    {g.items.map((n) => (
                      <NavLink key={n.to} n={n} active={loc.pathname === n.to} badge={badges[n.to]} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        )
        ) : (
          <div className="shrink-0 px-5 py-8" data-testid="practice-workspace-hint">
            {!collapsed && (<>
            <div className="text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-[#F37A24] mb-2">Practice workspace</div>
            <p className="text-sm text-white/70 leading-relaxed">
              Open a client — from the switcher above or your <Link to="/portfolio" className="text-white underline underline-offset-2 hover:text-white/80">Clients</Link> list — to access its ledgers, tasks, reconciliations and reports.
            </p>
            </>)}
          </div>
        )}
        <div className="mt-auto shrink-0">
        {inOrg && (
          <div className="px-2 py-2 border-t border-white/10 space-y-1">
            {ORG_BOTTOM.map((n) => (
              <NavLink key={n.to} n={n} active={loc.pathname === n.to} collapsed={collapsed} />
            ))}
          </div>
        )}
        <div className="px-2 py-2 border-t border-white/10 space-y-1">
          {PRACTICE_BOTTOM
            .filter((n) => !n.admin || user?.role === "admin")
            .filter((n) => !n.practiceOnly || !inOrg)
            .map((n) => (
            <NavLink key={n.to} n={n} active={loc.pathname === n.to} collapsed={collapsed} />
          ))}
        </div>
        <div className={`${collapsed ? "px-2" : "px-3"} py-2 border-t border-white/10`}>
          <button onClick={toggleCollapsed} data-testid="sidebar-collapse-toggle"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-2 px-3"} py-2 rounded-lg text-sm font-semibold text-[#F37A24] hover:bg-[#F37A24]/15 transition-colors`}>
            {collapsed ? <CaretDoubleRight size={20} weight="bold" /> : <><CaretDoubleLeft size={16} weight="bold" /> Collapse menu</>}
          </button>
        </div>
        <div className={`${collapsed ? "px-2" : "px-4"} py-4 border-t border-white/10`}>
          {!collapsed && <HealthDot />}
          {!collapsed && (
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs text-white/70 truncate" data-testid="current-user-email">{user?.email}</div>
            {user?.role === "viewer" && (
              <span data-testid="viewer-badge"
                className="shrink-0 text-[0.6rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange/20 text-orange">
                Read-only
              </span>
            )}
          </div>
          )}
          <Link to="/account" data-testid="nav-account" title="My account" aria-label="My account"
            className={`relative ${collapsed ? "group" : ""} w-full flex items-center justify-center gap-2 text-sm py-2 mb-2 rounded-lg border transition-colors duration-150 ${
              loc.pathname === "/account" ? "border-orange bg-orange/20 text-white" : "border-white/20 text-white/80 hover:bg-white/10"
            }`}>
            <UserCircle size={16} /> {!collapsed && "My account"}
            {collapsed && (
              <span data-testid="nav-tooltip-account"
                className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block z-[60] whitespace-nowrap bg-navy border border-white/15 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg shadow-xl pointer-events-none">
                My account
              </span>
            )}
          </Link>
          <button
            onClick={onLogout}
            data-testid="logout-btn" title="Sign out" aria-label="Sign out"
            className={`relative ${collapsed ? "group" : ""} w-full flex items-center justify-center gap-2 text-sm py-2 rounded-lg border border-white/20 text-white/80 hover:bg-white/10 transition-colors duration-150`}
          >
            <SignOut size={16} /> {!collapsed && "Sign out"}
            {collapsed && (
              <span data-testid="nav-tooltip-logout"
                className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block z-[60] whitespace-nowrap bg-navy border border-white/15 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg shadow-xl pointer-events-none">
                Sign out
              </span>
            )}
          </button>
        </div>
        </div>
      </aside>
      <main className={`flex-1 ${collapsed ? "ml-20" : "ml-64"} min-h-screen transition-[margin] duration-200`}>
        <TrialCountdownBanner account={account} onManage={() => nav("/subscription")} />
        <PastDueBanner account={account} />
        <OnboardingNudge account={account} pathname={loc.pathname} onGo={() => nav("/onboarding")} />
        {demoMode && (
          <div data-testid="demo-mode-banner"
            className="sticky top-0 z-30 flex items-center justify-center gap-3 bg-[#F37A24] text-white text-sm font-medium px-4 py-2 shadow-sm">
            <Sparkle size={15} weight="fill" />
            <span>Demo data loaded (WorkWithLottie) — clear before going live.</span>
            <button onClick={() => nav("/settings?tab=connect")} data-testid="demo-banner-clear"
              className="ml-1 px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-xs font-semibold uppercase tracking-wide">
              Manage / clear
            </button>
          </div>
        )}
        {children}
        <ImportIndicator />
        <GuidedFlow />
        {loc.pathname !== "/help" && (
          <AssistantChat pathname={loc.pathname} helpFocus={ROUTE_HELP[loc.pathname]} />
        )}
        {loc.pathname !== "/help" && loc.pathname !== "/learning" && (
          <HelpDrawer route={loc.pathname} />
        )}
      </main>
    </div>
  );
}


function HealthDot() {
  const [h, setH] = useState(null);
  useEffect(() => {
    const ping = () => api.get("/health").then(({ data }) => setH(data)).catch(() => setH({ status: "down" }));
    ping();
    const t = setInterval(ping, 60000);
    return () => clearInterval(t);
  }, []);
  const ok = h?.status === "ok" && h?.scheduler_running;
  const color = h == null ? "bg-white/30" : ok ? "bg-positive" : (h.status === "ok" ? "bg-warn" : "bg-negative");
  const label = h == null ? "Checking…" : ok ? "All systems healthy" : (h.status === "ok" ? "Scheduler idle" : "API unavailable");
  return (
    <div className="flex items-center gap-2 mb-3 text-[0.7rem] text-white/70" data-testid="health-indicator" title={label}>
      <span className={`inline-block w-2 h-2 rounded-full ${color} ${h == null ? "animate-pulse" : ""}`} data-testid="health-dot" />
      <span data-testid="health-label">{label}</span>
    </div>
  );
}

function CollapsedGroup({ g, badges, activePath }) {
  const groupBadge = g.items.reduce((s, it) => s + (badges[it.to] || 0), 0);
  return (
    <div className="relative group">
      <button title={g.title} data-testid={`nav-initial-${g.title.toLowerCase()}`}
        className="relative w-full flex items-center justify-center py-2 text-[0.72rem] font-bold uppercase tracking-widest text-white/60 rounded-lg hover:bg-white/10 hover:text-white transition-colors">
        {g.title.charAt(0)}
        {groupBadge > 0 && <span className="absolute top-1 right-1.5 w-2 h-2 rounded-full bg-orange ring-2 ring-navy" />}
      </button>
      <div data-testid={`nav-flyout-${g.title.toLowerCase()}`}
        className="absolute left-full top-0 z-50 hidden group-hover:block pl-2">
        <div className="bg-navy border border-white/15 rounded-xl shadow-xl p-2 min-w-[200px]">
          <div className="px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-widest text-white/60">{g.title}</div>
          <div className="space-y-1">
            {g.items.map((n) => (
              <NavLink key={n.to} n={n} active={activePath === n.to} badge={badges[n.to]} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NavLink({ n, active, badge, collapsed }) {
  const Icon = n.icon;
  return (
    <Link
      to={n.to}
      data-testid={n.id}
      aria-label={collapsed ? n.label : undefined}
      className={`relative flex items-center ${collapsed ? "justify-center px-2 py-2.5 group" : "gap-3 px-3 py-2"} text-sm rounded-lg transition-all duration-150 ${
        active ? "bg-orange text-white font-semibold shadow-card" : "text-white/65 hover:bg-white/10 hover:text-white"
      }`}
    >
      {active && (
        <span aria-hidden className="absolute -left-3 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full bg-[#F37A24]" data-testid={`nav-active-rail-${n.id}`} />
      )}
      <Icon size={collapsed ? 20 : 18} weight={active ? "fill" : "regular"} />
      {!collapsed && <span className="flex-1 truncate">{n.label}</span>}
      {collapsed && (
        <span data-testid={`nav-tooltip-${n.id}`}
          className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block z-[60] whitespace-nowrap bg-navy border border-white/15 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg shadow-xl pointer-events-none">
          {n.label}
        </span>
      )}
      {badge > 0 && (
        collapsed ? (
          <span data-testid={`nav-badge-${n.id}`} aria-label={`${badge} pending`}
            className="absolute top-1 right-1 min-w-[8px] h-[8px] rounded-full bg-orange ring-2 ring-navy" />
        ) : (
          <span data-testid={`nav-badge-${n.id}`}
            className={`min-w-[18px] h-[18px] px-1.5 rounded-full text-[0.62rem] font-bold flex items-center justify-center ${active ? "bg-white/25 text-white" : "bg-orange text-white"}`}>
            {badge > 99 ? "99+" : badge}
          </span>
        )
      )}
    </Link>
  );
}

function OrgSwitcher({ inOrg, collapsed }) {
  const [orgs, setOrgs] = useState([]);
  const [activeTid, setActiveTid] = useState(null);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/xero/status").then(({ data }) => {
      setOrgs(data.organisations || []);
      setActiveTid(data.active_tenant_id || null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (orgs.length === 0) return null;
  const active = orgs.find((o) => o.tenant_id === activeTid) || orgs[0];

  const enter = async (tid) => {
    setOpen(false);
    setSwitching(true);
    try {
      await api.post("/xero/organisations/activate", { tenant_id: tid });
      localStorage.setItem("lottie_workspace", "org");
      localStorage.setItem("lottie_collapse_nav", "1");
      window.location.assign("/dashboard");
    } catch { setSwitching(false); }
  };

  const single = orgs.length === 1;
  const initials = (active?.tenant_name || "")
    .replace(/[^a-zA-Z0-9 ]/g, " ").trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";

  const optionsMenu = (
    <>
      {orgs.map((o) => (
        <button key={o.tenant_id} onClick={() => enter(o.tenant_id)} data-testid={`org-option-${o.tenant_id}`}
          className={`w-full text-left px-3 py-2.5 text-sm transition-colors flex items-center gap-2 ${o.tenant_id === active?.tenant_id && inOrg ? "bg-orange/10 text-orange font-medium" : "text-navy hover:bg-navy/5"}`}>
          <span className="truncate">{o.tenant_name}</span>
          {o.demo && <span className="shrink-0 text-[0.55rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F37A24]/15 text-[#F37A24]">Demo</span>}
        </button>
      ))}
    </>
  );

  if (collapsed) {
    return (
      <div className="px-2 py-3 border-b border-white/10 relative flex justify-center" data-testid="org-switcher" ref={ref}>
        <button onClick={() => setOpen(!open)} disabled={switching || (single && inOrg)} data-testid="org-switcher-trigger"
          title={inOrg ? active?.tenant_name : "Select organisation"}
          className="w-10 h-10 rounded-lg bg-orange text-white text-xs font-bold flex items-center justify-center hover:bg-orange/80 transition-colors disabled:opacity-100">
          {switching ? "…" : (inOrg ? <span data-testid="active-org-initials">{initials}</span> : <Buildings size={18} />)}
        </button>
        {open && (
          <div className="absolute left-full top-0 ml-2 w-56 bg-white rounded-lg shadow-xl z-50 overflow-hidden border border-slate-200" data-testid="org-switcher-menu">
            {optionsMenu}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-b border-white/10 relative" data-testid="org-switcher" ref={ref}>
      <div className="overline text-white/70 mb-1.5 flex items-center gap-1.5">
        <Buildings size={12} /> {inOrg ? "Organisation" : "Open a client"}
      </div>
      {single && inOrg ? (
        <div className="text-sm font-medium text-white truncate flex items-center gap-2" data-testid="active-org-name">
          <span className="truncate">{active?.tenant_name}</span>
          {active?.demo && <span data-testid="org-demo-tag" className="shrink-0 text-[0.55rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F37A24] text-white">Demo</span>}
        </div>
      ) : (
        <button onClick={() => setOpen(!open)} disabled={switching} data-testid="org-switcher-trigger"
          className="w-full flex items-center justify-between gap-2 bg-white/5 hover:bg-white/10 rounded-lg px-3 py-2 text-sm text-white transition-colors">
          <span className="flex items-center gap-2 min-w-0">
            <span className="truncate" data-testid="active-org-name">{switching ? "Opening…" : (inOrg ? active?.tenant_name : "Select organisation")}</span>
            {inOrg && active?.demo && <span data-testid="org-demo-tag" className="shrink-0 text-[0.55rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F37A24] text-white">Demo</span>}
          </span>
          <CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      {open && (
        <div className="absolute left-4 right-4 mt-1 bg-white rounded-lg shadow-xl z-30 overflow-hidden border border-slate-200" data-testid="org-switcher-menu">
          {optionsMenu}
        </div>
      )}
    </div>
  );
}
